import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import {
  dispatchSpaceActionToolResultSchema,
  dispatchSpaceHistoryRequestSchema,
  dispatchSpaceHistoryResultSchema,
  listSpaceTasksResponseSchema,
  openMeldRuntimeProfileIdSchema,
  spaceFileEntryIdSchema,
  spaceFileRevisionNumberSchema,
  spaceMessageFileReferenceSchema,
  spaceTaskIdSchema,
  spaceTaskMutationReceiptSchema,
} from "@openmeld/schemas";
import { z } from "zod/v4";
import type { DispatchSpaceActionRecord } from "../runtime/dispatch-space-action-context";
import {
  DispatchSpaceActionClosedError,
  readDispatchSpaceActionContextForReceiptFromEnv,
} from "../runtime/dispatch-space-action-context";
import {
  recordDispatchSpaceReply,
  recordDispatchSpaceSilent,
  recordDispatchSpaceStatus,
} from "./action-command";
import { readDispatchSpaceFile } from "./dispatch-file-read";
import { readDispatchSpaceHistory } from "./dispatch-history-read";
import {
  createDispatchSpaceTask,
  listDispatchSpaceTasks,
  mutateDispatchSpaceTask,
} from "./dispatch-task";
import { runDispatchWorkingCopy } from "./dispatch-working-copy";

const FINAL_STATUS_SCHEMA = z.enum([
  "done",
  "blocked",
  "needs_input",
  "handoff",
]);

export function createSpaceActionMcpServer(): McpServer {
  const server = new McpServer(
    {
      name: "openmeld-dispatch-space-action",
      version: "1.0.0",
    },
    {
      instructions:
        "Use read_history for the bounded text history around the current Wake, and read_file for shared text files in the current Space. Use Space Task tools only when a clear deliverable needs durable ownership, progress, review, or later closure; questions and tiny same-turn actions stay in Messages. Task operations are non-final. Then choose exactly one final OpenMeld Space Action: reply for the exact public message, status for a final visible status, or stay_silent when no public message is needed. Private assistant output is not posted to the Space.",
    }
  );

  server.registerTool(
    "working_copy",
    {
      title: "Save or inspect this handoff's files",
      description:
        "Inspect or commit this handoff's files; reconcile retries an unknown save. On conflict, refresh downloads the shared version, then status shows its path and revision. Merge it into the original working file, then resolve with fileId and incoming revision before committing again. No automatic merge or overwrite. Does not deliver or close the Wake. No Space, profile or path overrides.",
      inputSchema: {
        action: z.enum(["status", "commit", "reconcile", "refresh", "resolve"]),
        fileId: z.string().optional(),
        revision: z.number().int().positive().optional(),
      },
    },
    async ({ action, fileId, revision }) => {
      try {
        return {
          content: [
            {
              type: "text" as const,
              text: JSON.stringify(
                await runDispatchWorkingCopy(action, { fileId, revision })
              ),
            },
          ],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: error instanceof Error ? error.message : String(error),
            },
          ],
        };
      }
    }
  );

  server.registerTool(
    "read_history",
    {
      title: "Read the current Wake's Space history",
      description:
        "Read up to 20 text messages before the current Wake, plus its source message. Space, Agent identity and source are fixed by the active dispatch. Use this instead of terminal CLI history. This read does not submit a reply or close the Wake.",
      inputSchema: dispatchSpaceHistoryRequestSchema,
      outputSchema: dispatchSpaceHistoryResultSchema,
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async () => {
      try {
        const result = await readDispatchSpaceHistory();
        return {
          structuredContent: result,
          content: [{ type: "text" as const, text: JSON.stringify(result) }],
        };
      } catch (error) {
        return {
          isError: true,
          content: [
            {
              type: "text" as const,
              text: error instanceof Error ? error.message : String(error),
            },
          ],
        };
      }
    }
  );

  server.registerTool(
    "read_file",
    {
      title: "Read a file in the current OpenMeld Space",
      description:
        "Read a shared text file as the Agent in the current Wake. Use this for attached CSV, text, Markdown, or JSON files instead of running the CLI in a terminal. Pass the attachment's file ID and revision. This is a context read; it does not submit a reply or close the Wake.",
      inputSchema: {
        fileId: spaceFileEntryIdSchema,
        revision: spaceFileRevisionNumberSchema.optional(),
      },
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async (input) => {
      try {
        const result = await readDispatchSpaceFile(input);
        return {
          content: [{ type: "text" as const, text: JSON.stringify(result) }],
        };
      } catch (error) {
        return {
          content: [
            {
              type: "text" as const,
              text: error instanceof Error ? error.message : String(error),
            },
          ],
          isError: true,
        };
      }
    }
  );

  server.registerTool(
    "list_tasks",
    {
      title: "List Tasks in the current OpenMeld Space",
      description:
        "Read durable collaboration Tasks for this Space. Use the returned revision before changing a Task. Task phase is collaboration state, not Agent execution truth.",
      inputSchema: {
        phase: z
          .enum(["to_do", "in_progress", "in_review", "done", "canceled"])
          .optional(),
      },
      outputSchema: listSpaceTasksResponseSchema,
      annotations: {
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async ({ phase }) => executeTaskAction(() => listDispatchSpaceTasks(phase))
  );

  server.registerTool(
    "create_task",
    {
      title: "Create a Task from the current Space message",
      description:
        "Create a durable Task only for a clear deliverable that needs ownership, progress, review, or later closure. The source message and Space are fixed by this Wake. The Task is assigned to the current Agent by default and remains To do until start_task is called.",
      inputSchema: {
        title: z.string().trim().min(1).max(200),
        description: z.string().trim().min(1).max(8000).nullable().optional(),
        completionConditions: z
          .array(z.string().trim().min(1).max(500))
          .max(12)
          .optional(),
        assigneeProfileId: openMeldRuntimeProfileIdSchema.nullable().optional(),
        reviewerProfileId: openMeldRuntimeProfileIdSchema.nullable().optional(),
      },
      outputSchema: spaceTaskMutationReceiptSchema,
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
      },
    },
    async (body) => executeTaskAction(() => createDispatchSpaceTask(body))
  );

  server.registerTool(
    "start_task",
    {
      title: "Mark a Space Task in progress",
      description:
        "Record that the current assigned Agent has begun advancing this Task. This changes collaboration state and does not start a separate Agent execution.",
      inputSchema: {
        taskId: spaceTaskIdSchema,
        expectedRevision: z.number().int().min(1),
      },
      outputSchema: spaceTaskMutationReceiptSchema,
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
      },
    },
    async ({ expectedRevision, taskId }) =>
      executeTaskAction(() =>
        mutateDispatchSpaceTask({
          taskId,
          body: { action: "mark_in_progress", expectedRevision },
        })
      )
  );

  server.registerTool(
    "submit_task_result",
    {
      title: "Submit a result for a Space Task",
      description:
        "Submit the concrete result and verification for an in-progress Task. A Task with a reviewer moves to In review; otherwise the stated completion boundary moves it to Done. If the reviewer is an Agent, use your one final reply to wake that reviewer.",
      inputSchema: {
        taskId: spaceTaskIdSchema,
        expectedRevision: z.number().int().min(1),
        summary: z.string().trim().min(1).max(8000),
      },
      outputSchema: spaceTaskMutationReceiptSchema,
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
      },
    },
    async ({ expectedRevision, summary, taskId }) =>
      executeTaskAction(() =>
        mutateDispatchSpaceTask({
          taskId,
          body: { action: "submit_result", expectedRevision, summary },
        })
      )
  );

  server.registerTool(
    "review_task",
    {
      title: "Review a Space Task result",
      description:
        "Accept the current result or request specific changes as this Task's reviewer. Change requests require feedback. If the assignee is an Agent and the Task returns to In progress, use your one final reply to wake that assignee with the feedback. Do not wake more work when needsHumanDecision is true. Agent-to-Agent rework stops for a human decision after two automatic rounds.",
      inputSchema: {
        taskId: spaceTaskIdSchema,
        expectedRevision: z.number().int().min(1),
        decision: z.enum(["accepted", "changes_requested"]),
        feedback: z.string().trim().min(1).max(8000).nullable().optional(),
      },
      outputSchema: spaceTaskMutationReceiptSchema,
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
      },
    },
    async ({ decision, expectedRevision, feedback, taskId }) =>
      executeTaskAction(() =>
        mutateDispatchSpaceTask({
          taskId,
          body: {
            action: "review",
            expectedRevision,
            decision,
            ...(feedback === undefined ? {} : { feedback }),
          },
        })
      )
  );

  server.registerTool(
    "cancel_task",
    {
      title: "Cancel a Space Task",
      description:
        "Cancel a Task when its accepted commitment should no longer be done. Cancellation fences later Task progress; it does not by itself prove that an Agent execution stopped.",
      inputSchema: {
        taskId: spaceTaskIdSchema,
        expectedRevision: z.number().int().min(1),
        reason: z.string().trim().min(1).max(1000),
      },
      outputSchema: spaceTaskMutationReceiptSchema,
      annotations: {
        readOnlyHint: false,
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
      },
    },
    async ({ expectedRevision, reason, taskId }) =>
      executeTaskAction(() =>
        mutateDispatchSpaceTask({
          taskId,
          body: { action: "cancel", expectedRevision, reason },
        })
      )
  );

  server.registerTool(
    "reply",
    {
      title: "Reply in the current OpenMeld Space",
      description:
        "Record the exact final public reply for the active OpenMeld Wake. Use real newline characters for paragraph breaks. Optionally wake or reference an Agent, or mention a person, by mention label or profile ID.",
      inputSchema: {
        message: z.string().min(1),
        files: z.array(spaceMessageFileReferenceSchema).max(10).optional(),
        mentionTargets: z.array(z.string().min(1)).optional(),
        referenceTargets: z.array(z.string().min(1)).optional(),
        wakeTargets: z.array(z.string().min(1)).optional(),
      },
      outputSchema: dispatchSpaceActionToolResultSchema,
      annotations: {
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
        readOnlyHint: false,
      },
    },
    async ({ files, mentionTargets, message, referenceTargets, wakeTargets }) =>
      executeSpaceAction(() =>
        recordDispatchSpaceReply({
          files,
          mentionTargets,
          referenceTargets,
          text: message,
          wakeTargets,
        })
      )
  );

  server.registerTool(
    "status",
    {
      title: "Set the final OpenMeld Space status",
      description:
        "Record one final visible status for the active OpenMeld Wake. This closes the Wake; do not use it for interim progress.",
      inputSchema: {
        status: FINAL_STATUS_SCHEMA,
        text: z.string().min(1),
      },
      outputSchema: dispatchSpaceActionToolResultSchema,
      annotations: {
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
        readOnlyHint: false,
      },
    },
    async ({ status, text }) =>
      executeSpaceAction(() => recordDispatchSpaceStatus({ status, text }))
  );

  server.registerTool(
    "stay_silent",
    {
      title: "Stay silent in the current OpenMeld Space",
      description:
        "Record that the active OpenMeld Wake intentionally needs no public reply. Use this only after deciding that silence is the correct final outcome.",
      inputSchema: {
        reason: z.string().min(1),
      },
      outputSchema: dispatchSpaceActionToolResultSchema,
      annotations: {
        destructiveHint: false,
        idempotentHint: false,
        openWorldHint: false,
        readOnlyHint: false,
      },
    },
    async ({ reason }) =>
      executeSpaceAction(() => recordDispatchSpaceSilent({ reason }))
  );

  return server;
}

export async function runSpaceActionMcpServer(): Promise<void> {
  readDispatchSpaceActionContextForReceiptFromEnv();
  const server = createSpaceActionMcpServer();
  await server.connect(new StdioServerTransport());
}

export async function executeSpaceAction(
  action: () => DispatchSpaceActionRecord | Promise<DispatchSpaceActionRecord>
) {
  try {
    const context = readDispatchSpaceActionContextForReceiptFromEnv();
    const record = await action();
    if (
      record.contextId !== context.contextId ||
      record.nonce !== context.nonce
    ) {
      throw new DispatchSpaceActionClosedError(
        "space_action.context_mismatch",
        "The saved action does not match the current dispatch context."
      );
    }
    const contextState =
      Date.now() > context.expiresAtMs ? "expired" : "active";
    const structuredContent = dispatchSpaceActionToolResultSchema.parse({
      receipt: {
        status: "proposal_saved_locally",
        actionType: record.action.type,
        attemptId: context.attemptId,
        contextId: context.contextId,
        dispatchId: context.dispatchId,
        recordedAtMs: record.createdAtMs,
        contextState,
        coordinatorAcceptance: "unconfirmed",
        publication: "unconfirmed",
      },
    });
    return {
      structuredContent,
      content: [
        {
          type: "text" as const,
          text: `Local proposal saved (receipt: proposal_saved_locally, action: ${record.action.type}). OpenMeld has recorded this unpublished Space action proposal on this computer. ${contextState === "expired" ? "The local context has expired. This receipt identifies the existing record and does not extend action authority. " : ""}Coordinator acceptance and Space publication are NOT yet confirmed; do not report it as posted.`,
        },
      ],
    };
  } catch (error) {
    if (error instanceof DispatchSpaceActionClosedError) {
      return {
        structuredContent: dispatchSpaceActionToolResultSchema.parse({
          receipt: {
            status: "rejected_closed",
            reasonCode: error.reasonCode,
            coordinatorAcceptance: "unconfirmed",
            publication: "unconfirmed",
          },
        }),
        content: [
          {
            type: "text" as const,
            text: `Action rejected (receipt: rejected_closed, reason: ${error.reasonCode}). ${error.message} New actions are not permitted in this context. Existing records are preserved; Coordinator acceptance and Space publication are unconfirmed.`,
          },
        ],
        isError: true,
      };
    }
    return {
      structuredContent: dispatchSpaceActionToolResultSchema.parse({
        receipt: {
          status: "outcome_unknown",
          reasonCode: "space_action.recording_outcome_unknown",
          coordinatorAcceptance: "unconfirmed",
          publication: "unconfirmed",
        },
      }),
      content: [
        {
          type: "text" as const,
          text: `Action receipt unavailable (receipt: outcome_unknown). ${error instanceof Error ? error.message : String(error)} Existing records are preserved; Coordinator acceptance and Space publication are unconfirmed.`,
        },
      ],
      isError: true,
    };
  }
}

async function executeTaskAction<T extends Record<string, unknown>>(
  action: () => Promise<T>
) {
  try {
    const result = await action();
    return {
      structuredContent: result,
      content: [{ type: "text" as const, text: JSON.stringify(result) }],
    };
  } catch (error) {
    return {
      isError: true,
      content: [
        {
          type: "text" as const,
          text: error instanceof Error ? error.message : String(error),
        },
      ],
    };
  }
}
